import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { DataService } from '../../services/data.service';
import * as d3 from 'd3';
import { constants } from 'projects/marketpro-ui/src/app/constants';
import { SizeOfPrize } from '../../models/statistics.model';
import { DataFiltersService } from '../../services/data-filters.service';
import { AuthService } from '../../../auth/services/auth.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'mpro-treemap',
  templateUrl: './treemap.component.html',
  styleUrls: ['./treemap.component.scss'],
})
export class TreeMap implements OnInit {
  public legendItems = constants.legendItems;
  @Input() public countries: SizeOfPrize[] = [];
  public treemapData: any = [];
  private currentYear: number;
  private ngUnsubscribe: Subject<void> = new Subject();
  public isFreemium: boolean;

  constructor(
    public dataService: DataService,
    private dataFiltersService: DataFiltersService,
    private authService: AuthService
  ) {}

  public ngOnInit() {
    this.dataFiltersService.getDataFilters().subscribe((dataFilter) => {
      this.currentYear = dataFilter.year;
    });
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => (this.isFreemium = plan.isFreemium));
    this.treemapData = [];
  }

  public ngOnDestroy() {
    this.treemapData = [];
  }

  public destroyChart() {
    d3.select('#treemap').selectAll('svg').remove();
  }

  groupByContinentName(
    array: Array<{ continentName: string; [key: string]: any }>
  ) {
    const result = array.reduce((acc, curr) => {
      const { continentName } = curr;
      if (!acc[continentName]) {
        acc[continentName] = { name: continentName, children: [] };
      }
      acc[continentName].children.push({
        ...curr,
        year: this.currentYear,
        color: constants.legendItems.find(
          (continentColor) => continentColor.text === curr.continentName
        )?.color,
      });
      return acc;
    }, {} as { [key: string]: { name: string; children: Array<{ continentName: string; [key: string]: any }> } });

    return { name: 'continents', children: Object.values(result) };
  }

  ngOnChanges() {
    this.destroyChart();
    this.treemapData = [];
    if (this.isFreemium) {
      const formattedData = this.countries.map((continent) => ({
        ...continent,
        year: this.currentYear,
        color: constants.legendItems.find(
          (continentColor) => continentColor.text === continent.continentName
        )?.color,
      }));
      const treemapData = {
        name: 'Treemap data',
        children: formattedData,
      };
      this.treemapData = treemapData;
    } else {
      const treemapData = this.groupByContinentName(this.countries);
      this.treemapData = treemapData;
    }
    this.treemapData && this.drawChart();
  }

  protected drawChart() {
    const treemapContainer: HTMLDivElement | null = document.querySelector(
      '#treemap'
    ) as HTMLDivElement;

    const width = treemapContainer?.clientWidth;
    const height = 456;

    // append the svg object to the body of the page
    const chartSvg: any = d3
      .select(treemapContainer)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const g = chartSvg.append('g');
    // Give the data to this cluster layout:
    const root = d3
      .hierarchy(this.treemapData)
      .sum(function (d: any) {
        return d.totalAmount;
      })
      .sort((a: any, b: any) => b.totalAmount - a.totalAmount);
    // Apply zoom and drag

    function zoomed({ transform }) {
      g.attr('transform', transform);
    }
    const zoom = d3.zoom().scaleExtent([1, 40]).on('zoom', zoomed);
    chartSvg.call(zoom);

    d3.select('#zoom-in').on('click', function () {
      chartSvg.transition().call(zoom.scaleBy, 2);
    });

    d3.select('#zoom-out').on('click', function () {
      chartSvg.transition().call(zoom.scaleBy, 0.5);
    });

    // Then d3.treemap computes the position of each element of the hierarchy
    d3.treemap().size([width, height]).padding(1).round(true)(root);

    // use this information to add rectangles:
    g.selectAll('rect')
      .data(root.leaves())
      .enter()
      .append('rect')
      .attr('class', 'tree-border')
      .attr('x', function (d: any) {
        return d.x0;
      })
      .attr('y', function (d: any) {
        return d.y0;
      })
      .attr('rx', 0.5)
      .attr('width', function (d: any) {
        return d.x1 - d.x0;
      })
      .attr('height', function (d: any) {
        return d.y1 - d.y0;
      })
      .style('fill', (d: any) => d.data.color)
      .on('mouseover', function (event, d) {
        d3.select(this).style('stroke', 'black');
        const tooltip = d3.select('.tooltip');
        tooltip.style('opacity', 1);
        tooltip
          .html(
            `
              <h3>${
                this.isFreemium ? d.data.continentName : d.data.countryName
              } in ${d.data.year}</h3>
              <div style="display: flex; justify-content: space-between">
                <div>
                  <strong>${d.data.population.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}</strong>
                  <p>Population</p>
                </div>
                <div style="text-align: right">
                  <strong>
                  $${d.data.totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  </strong>
                  <p>Total spending in USD</p>
                </div>
              </div>
           `
          )
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).style('stroke', '#fff');
        d3.select('.tooltip').style('opacity', 0);
      })
      .on('mousemove', function (event) {
        d3.select('.tooltip')
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      });

    // and to add the text labels
    g.selectAll('text')
      .data(root.leaves())
      .enter()
      .append('text')
      .attr('x', (d: any) => d.x0 + 5)
      .attr('y', (d: any) => d.y1 - 5)
      .attr('visibility', (d) =>
        d.x1 - d.x0 < 35 || d.y1 - d.y0 < 35 ? 'hidden' : 'visible'
      )
      .text((d) => {
        return d.x1 - d.x0 < 65
          ? `${(this.isFreemium
              ? d.data.continentName
              : d.data.countryName
            ).slice(0, (d.x1 - d.x0) / 8 - 3)}...`
          : this.isFreemium
          ? d.data.continentName
          : d.data.countryName;
      })
      .attr('font-size', 14)
      .attr('fill', '#252525');
  }
}
